import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { JobHttpService } from 'cms/features/feature_job/service';
import {
    Box,
    Grid,
    Typography,
    Button,
    CircularProgress,
    Alert,
    Paper,
    TextField,
    InputAdornment,
    Pagination,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import CandidatesFilterSidebar from 'features/feature_job/components/CandidatesFilterSidebar';
import CandidatePeopleCard from 'features/feature_job/components/CandidatePeopleCard';

function EmployerCandidatesPage() {
    const navigate = useNavigate();
    const [page, setPage] = useState(1);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [filters, setFilters] = useState({
        country: '',
        city: '',
        skill: '',
        experience_level: '',
        title: '',
    });
    const [appliedFilters, setAppliedFilters] = useState({});
    const [searchQuery, setSearchQuery] = useState('');

    const sanitizeFilters = (f = {}) =>
        Object.entries(f)
            .filter(([, value]) => value !== undefined && value !== null && value !== '')
            .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

    const cleanedFilters = useMemo(() => {
        const sanitized = sanitizeFilters(appliedFilters);
        if (searchQuery.trim()) {
            sanitized.search = searchQuery.trim();
        }
        return sanitized;
    }, [appliedFilters, searchQuery]);

    const { data, isLoading, isError } = useQuery(
        ['employerCandidates', page, cleanedFilters],
        () => JobHttpService.loadCandidates({ page, size: 12, filters: cleanedFilters }),
        {
            keepPreviousData: true,
            staleTime: 30000,
        }
    );

    const handleViewCandidate = (candidateId) => {
        navigate(`/employer/candidates/${candidateId}`);
    };

    const handleApplyFilters = (newFilters) => {
        setAppliedFilters(newFilters);
        setPage(1);
    };

    const handleResetFilters = () => {
        setFilters({
            country: '',
            city: '',
            skill: '',
            experience_level: '',
            title: '',
        });
        setAppliedFilters({});
        setSearchQuery('');
        setPage(1);
    };

    const handleRemoveFilter = (key) => {
        setAppliedFilters((prev) => {
            const next = { ...prev };
            delete next[key];
            return next;
        });
        setFilters((prev) => ({
            ...prev,
            [key]: '',
        }));
        if (key === 'search') {
            setSearchQuery('');
        }
    };

    const candidates = data?.results || [];
    const totalCount = data?.count || 0;
    const totalPages = Math.ceil(totalCount / 12);

    return (
        <Box>
            <Box sx={{ mb: 3 }}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        mb: 2,
                        flexWrap: 'wrap',
                        gap: 2,
                    }}
                >
                    <Box>
                        <Typography variant="h4" fontWeight={700} gutterBottom>
                            Candidates
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Discover and connect with talented professionals
                        </Typography>
                    </Box>
                    <Button
                        variant={filtersOpen ? 'contained' : 'outlined'}
                        size="medium"
                        startIcon={<FilterListIcon />}
                        onClick={() => setFiltersOpen((o) => !o)}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        {filtersOpen ? 'Hide filters' : 'Filters'}
                    </Button>
                </Box>

                <TextField
                    fullWidth
                    placeholder="Search candidates by name, skills, or job title..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                            handleApplyFilters({ ...appliedFilters, search: searchQuery.trim() });
                        }
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon color="action" />
                            </InputAdornment>
                        ),
                        endAdornment: searchQuery ? (
                            <InputAdornment position="end">
                                <Button
                                    size="small"
                                    onClick={() => {
                                        setSearchQuery('');
                                        handleRemoveFilter('search');
                                    }}
                                >
                                    Clear
                                </Button>
                            </InputAdornment>
                        ) : null,
                    }}
                />
            </Box>

            {isLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress />
                </Box>
            )}

            {isError && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    Failed to load candidates. Please try again later.
                </Alert>
            )}

            {!isLoading && !isError && (
                <Grid container spacing={3} alignItems="flex-start">
                    {filtersOpen && (
                        <Grid item xs={12} md={4} lg={3}>
                            <Box sx={{ position: { md: 'sticky' }, top: { md: 80 } }}>
                                <CandidatesFilterSidebar
                                    filters={filters}
                                    onFilterChange={(name, value) => setFilters((prev) => ({ ...prev, [name]: value }))}
                                    onApplyFilters={handleApplyFilters}
                                    onResetFilters={handleResetFilters}
                                    appliedFilters={appliedFilters}
                                    onRemoveFilter={handleRemoveFilter}
                                />
                            </Box>
                        </Grid>
                    )}

                    <Grid item xs={12} md={filtersOpen ? 8 : 12} lg={filtersOpen ? 9 : 12}>
                        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                                {totalCount} {totalCount === 1 ? 'candidate' : 'candidates'} found
                            </Typography>
                        </Box>

                        {candidates.length === 0 ? (
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 6,
                                        textAlign: 'center',
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 2,
                                    }}
                                >
                                    <FilterListIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                                    <Typography variant="h6" color="text.secondary" gutterBottom>
                                        No candidates found
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Try adjusting your filters or search criteria
                                    </Typography>
                                </Paper>
                            ) : (
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 200px), 1fr))',
                                        gap: 2.5,
                                    }}
                                >
                                    {candidates.map((candidate) => (
                                        <Box
                                            key={candidate.id}
                                            sx={{
                                                width: '100%',
                                                minWidth: 0,
                                                display: 'flex',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <CandidatePeopleCard
                                                candidate={candidate}
                                                onCardClick={() => handleViewCandidate(candidate.id)}
                                                onPrimaryClick={() => handleViewCandidate(candidate.id)}
                                                primaryLabel="View profile"
                                            />
                                        </Box>
                                    ))}
                                </Box>
                            )}

                        {totalPages > 1 && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                                <Pagination
                                    count={totalPages}
                                    page={page}
                                    onChange={(event, value) => setPage(value)}
                                    color="primary"
                                    size="large"
                                />
                            </Box>
                        )}
                    </Grid>
                </Grid>
            )}
        </Box>
    );
}

export default EmployerCandidatesPage;
